<template>
  <span class="view-list" :id="field.key">
    <!-- Si es array: mostrar cada ítem sin unir por coma (el padre debería iterar y pasar un valor por vez; si llega array, mostramos cada uno) -->
    <span v-if="field.isArray && Array.isArray(displayValue)" class="list-items-wrapper">
      <span
        v-for="(val, index) in displayValue"
        :key="index"
        class="list-item"
      >
        {{ getDisplayLabel(val) }}
      </span>
    </span>
    <!-- Valor simple -->
    <span v-else>
      {{ getDisplayLabel(displayValue) }} 
    </span>
  </span>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { getTranslatedListLabel } from "@/utils/listItemI18n";

export default {
  name: "ViewFieldList",
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Object, Array],
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
    modelName: {
      type: String,
      default: "",
    },
    isGroupModel: {
      type: Boolean,
      default: false,
    },
    lists: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      listOptions: [],
    };
  },
  computed: {
    /** Reactividad al cambiar idioma de la UI */
    i18nLocale() {
      return (this.$i18n && this.$i18n.locale) || "es";
    },
    displayValue() {
      if (this.value !== null && this.value !== undefined && this.value !== "") {
        return this.value;
      }
      if (this.field.defaultValue !== undefined && this.field.defaultValue !== null) {
        return this.field.defaultValue;
      }
      return null;
    },
  },
  mounted() {
    this.loadListOptions();
  },
  watch: {
    i18nLocale() {
      this.$forceUpdate();
    },
    lists: {
      handler() {
        this.loadListOptions();
      },
      deep: true,
    },
    "field.listType"() {
      this.loadListOptions();
    },
  },
  methods: {
    async loadListOptions() {
      if (!this.field.listType) {
        this.listOptions = [];
        return;
      }
      const existingList = this.lists.find((l) => l.type === this.field.listType);
      if (existingList && existingList.values) {
        this.listOptions = existingList.values;
        return;
      }
      try {
        const response = await wencesApi.get(
          `/api/gotrading/lists/type/${this.field.listType}`
        );
        if (response.data && response.data.values) {
          this.listOptions = response.data.values;
        } else {
          this.listOptions = [];
        }
      } catch (error) {
        console.error(
          `Error loading list options for ${this.field.listType}:`,
          error
        );
        this.listOptions = [];
      }
    },
    /** Objeto de lista (con labels) preferente: definición cargada o valor embebido */
    resolveListOption(value) {
      if (!value || typeof value !== "object") return null;
      const id = value._id || value.id;
      if (id && this.listOptions.length > 0) {
        const option = this.listOptions.find((opt) => {
          const optId = opt._id || opt.id;
          return optId && optId.toString() === String(id);
        });
        if (option) {
          const optL =
            option.labels && typeof option.labels === "object" && !Array.isArray(option.labels)
              ? option.labels
              : {};
          const valL =
            value.labels && typeof value.labels === "object" && !Array.isArray(value.labels)
              ? value.labels
              : {};
          return {
            ...option,
            label: value.label != null && value.label !== "" ? value.label : option.label,
            labels: { ...optL, ...valL },
          };
        }
      }
      return value;
    },
    /** Cadena "Padre > Hijo > ..." usando la definición de la lista (parent en listOptions), o el label plano. */
    hierarchyLabelForId(idOrValue, loc) {
      if (!idOrValue || this.listOptions.length === 0) return null;
      const id =
        typeof idOrValue === "object" && idOrValue !== null
          ? idOrValue._id || idOrValue.id
          : idOrValue;
      if (id == null || id === "") return null;
      const chain = this.buildHierarchyItems(id);
      if (chain.length > 1) {
        return chain.map((it) => getTranslatedListLabel(it, loc)).join(" > ");
      }
      if (chain.length === 1) {
        return getTranslatedListLabel(chain[0], loc);
      }
      return null;
    },
    getDisplayLabel(value) {
      if (!value) return "";
      const loc = this.i18nLocale;
      if (typeof value === "object" && value !== null) {
        if (value.hierarchy && typeof value.hierarchy === "string") {
          return value.hierarchy;
        }
        const hier = this.hierarchyLabelForId(value, loc);
        if (hier) {
          return hier;
        }
        if (value.label !== undefined && value.label !== null) {
          const resolved = this.resolveListOption(value);
          return getTranslatedListLabel(resolved || value, loc);
        }
        if (value._id) {
          const option = this.resolveListOption(value);
          if (option) {
            return getTranslatedListLabel(option, loc);
          }
        }
      }
      const valueId = typeof value === "object" ? value._id || value.id : value;
      if (valueId && this.listOptions.length > 0) {
        const hier = this.hierarchyLabelForId(valueId, loc);
        if (hier) {
          return hier;
        }
        const option = this.listOptions.find((opt) => {
          const optId = opt._id || opt.id;
          return optId && optId.toString() === String(valueId);
        });
        if (option) {
          return getTranslatedListLabel(option, loc);
        }
      }
      return String(valueId || value || "");
    },
    /** Cadena de ítems desde raíz hasta el id (para listOptions planas) */
    buildHierarchyItems(itemId, visited = new Set()) {
      if (!itemId || visited.has(String(itemId))) {
        return [];
      }
      visited.add(String(itemId));
      const item = this.listOptions.find((opt) => {
        const optId = opt._id || opt.id;
        return optId && optId.toString() === String(itemId);
      });
      if (!item) {
        return [];
      }
      if (item.parent) {
        let parentId;
        if (typeof item.parent === "object" && item.parent.toString) {
          parentId = item.parent.toString();
        } else {
          parentId = String(item.parent);
        }
        const parentChain = this.buildHierarchyItems(parentId, visited);
        return [...parentChain, item];
      }
      return [item];
    },
  },
};
</script>

<style scoped>
.view-list {
  font-weight: 500;
  word-break: break-word;
}

.list-items-wrapper .list-item {
  display: inline;
}
.list-items-wrapper .list-item + .list-item {
  margin-left: 0.35em;
}
</style>

